<template>
  <div id="listWidget" class="mb-5">
    <!-- 사용 안내 -->
    <div class="instruction-card card-item mb-3">
      <div class="instruction-header">
        <i class="fa-solid fa-info-circle me-2 text-primary"></i>
        <span class="instruction-title">사용 안내</span>
      </div>
      <div class="instruction-body">
        <div class="instruction-step">
          <div class="step-number">1</div>
          <div class="step-content">
            <span>교체할 컨트롤러를 선택해주세요.</span>
          </div>
        </div>
        <div class="instruction-step">
          <div class="step-number">2</div>
          <div class="step-content">
            <span>선택한 컨트롤러 타입에 따라 다음 단계로 진행됩니다.</span>
            <div class="mt-2">
              <div class="mb-1">
                <strong class="text-primary">조명제어기</strong>: 세팅 모드 진입 → AP 연결 → WiFi 설정 → 서버 등록
              </div>
              <div>
                <strong class="text-primary">온도조절기</strong>: LED 확인 및 QR 코드 스캔 → WiFi 연결 및 설정 → 서버 등록
              </div>
            </div>
          </div>
        </div>
        <div class="instruction-warning">
          <span style="color: #dc3545; font-size: 0.8rem;">
            <i class="fa-solid fa-triangle-exclamation me-1"></i>
            주의) 조명제어기 설정시 한 장치만 활성화 되어 있어야합니다.
          </span>
        </div>
      </div>
    </div>

    <!-- <hr class="mb-4" v-if="controllers.length > 0" /> -->

    <div class="row">
      <ControllerItem
        v-for="item in controllers"
        :key="item._id"
        :data="item"
        :isSelect="true"
        @onChecked="onSelectController"
      />
    </div>
  </div>

  <div id="footer" class="app-footer ms-0">
    <footer class="mt-auto">
      <div class="row">
        <div class="col-12">
          <button
            type="button"
            class="btn btn-primary btn-lg w-100"
            @click="onChangeControllerClick"
          >
            다음
          </button>
        </div>
      </div>
    </footer>
  </div>

  <div v-if="meshWaiting" class="mesh-waiting-overlay">
    <div class="mesh-waiting-card">
      <div class="spinner-border text-primary mesh-waiting-spinner" role="status" />
      <p
        v-for="(line, index) in meshProgressLines"
        :key="index"
        class="mesh-waiting-text"
      >
        {{ line }}
      </p>
    </div>
  </div>
</template>
<script setup>
import {
  ref,
  reactive,
  getCurrentInstance,
  onBeforeMount,
  computed,
  onBeforeUnmount,
} from 'vue';
import { useStore } from 'vuex';
import Axios, { ZoneApi, CtrlApi } from '@/api';

import { useRouter } from 'vue-router';

import ControllerItem from './components/ControllerItem.vue';
import useMqttControl from '@/common/hooks/useMqttControl';

const mqttControl = reactive(useMqttControl());
const { emitter } = getCurrentInstance().appContext.config.globalProperties;
const router = useRouter();
const store = useStore();
const controllers = computed(() => store.getters['home/getControllers']);
const zones = computed(() => store.getters['home/getZones']);
const selectedController = ref(undefined);

const MESH_POLL_INTERVAL_MS = 5000;
const MESH_POLL_MAX_MS = 60000;
const meshGuideSteps = [
  { text: '기존의 컨트롤러를 제거하고 새로운 컨트롤러를 설치하세요.' },
  {
    text: '기존과 동일한 MESH에 등록하세요.',
    sub: '(부모 Router가 다르면 등록되지 않습니다.)',
  },
  { text: '1분내 MESH에 등록하면 자동으로 교체 처리됩니다.' },
];

const meshProgressLines = [
  '교체된 컨트롤러를 찾고 있습니다.',
  '잠시만 기다려 주세요',
];

const getMeshGuideDescription = () =>
  meshGuideSteps
    .map((step, index) => {
      const lines = [`${index + 1}. ${step.text}`];
      if (step.sub) {
        lines.push(step.sub);
      }
      return lines.join('\n');
    })
    .join('\n');

const meshWaiting = ref(false);

const isMeshSsid = (ssid) => ssid != null && String(ssid).toUpperCase() === 'MESH';

const getSelectedController = () =>
  controllers.value.find((c) => c._id === selectedController.value);

const onSelectController = (id) => {
  selectedController.value = id;
};

const pollMeshTempController = async (oldController) => {
  const startTime = Date.now();
  while (Date.now() - startTime < MESH_POLL_MAX_MS) {
    try {
      const result = await Axios.get(
        CtrlApi.TEMP_MESH_CTRL,
        {
          model: oldController.model,
          parentController: oldController.parentController,
          excludeId: oldController._id,
        },
        '',
        { hideLoading: true },
      );
      if (result.resultCode === '0000' && result.resultData?._id) {
        return result.resultData;
      }
    } catch (error) {
      console.log(error);
    }
    if (Date.now() - startTime + MESH_POLL_INTERVAL_MS >= MESH_POLL_MAX_MS) {
      break;
    }
    await new Promise((resolve) => setTimeout(resolve, MESH_POLL_INTERVAL_MS));
  }
  return null;
};

const executeMeshControllerChange = async (oldController, newTempController) => {
  const changeBody = {
    _id: newTempController._id,
    version: newTempController.version,
    model: newTempController.model,
  };
  if (oldController.parentController) {
    changeBody.parentController = oldController.parentController;
  }
  if (oldController.meshId != null) {
    changeBody.meshId = oldController.meshId;
  }
  if (oldController.ssid) {
    changeBody.ssid = oldController.ssid;
  }
  return Axios.patch(
    CtrlApi.CHANGE_CTRL.replace(':id', oldController._id),
    changeBody,
    '',
    { hideLoading: true },
  );
};

const startMeshChangeProcess = async (oldController) => {
  meshWaiting.value = true;
  try {
    const newTempController = await pollMeshTempController(oldController);

    if (!newTempController) {
      emitter.emit('showPopAlert', {
        description:
          '컨트롤러 교체에 실패했습니다.\n1분 내 새 컨트롤러를 찾지 못했습니다.',
      });
      return;
    }

    const changeResult = await executeMeshControllerChange(oldController, newTempController);
    if (changeResult.resultCode === '0000') {
      await store.dispatch('home/fetchAllZones');
      emitter.emit('showPopAlert', {
        description: '컨트롤러가 교체되었습니다.',
        okCallback: () => router.go(-1),
        cancelCallback: () => router.go(-1),
      });
    } else {
      emitter.emit('showPopAlert', {
        description: changeResult.resultMessage || '컨트롤러 교체에 실패했습니다.',
      });
    }
  } catch (error) {
    console.log(error);
    emitter.emit('showPopAlert', {
      description: '컨트롤러 교체 중 오류가 발생하였습니다.',
    });
  } finally {
    meshWaiting.value = false;
  }
};

const onChangeControllerClick = async () => {
  if (!selectedController.value) {
    emitter.emit('showPopAlert', {
      description: '교체할 컨트롤러를 선택해주세요.',
    });
    return;
  }

  const selected = getSelectedController();
  if (selected && isMeshSsid(selected.ssid)) {
    if (!selected.parentController) {
      emitter.emit('showPopAlert', {
        description: 'MESH 부모 컨트롤러 정보가 없습니다.',
      });
      return;
    }
    emitter.emit('showPopAlert', {
      description: getMeshGuideDescription(),
      okCallback: () => startMeshChangeProcess(selected),
    });
    return;
  }

  // 선택한 컨트롤러가 속한 zone 찾기
  const selectedZone = zones.value.find(zone => 
    zone.controllers.some(controller => controller._id === selectedController.value)
  );

  if (!selectedZone) {
    emitter.emit('showPopAlert', {
      description: '컨트롤러가 속한 영역을 찾을 수 없습니다.',
    });
    return;
  }

  // 세팅모드가 활성화되어 있지 않은 경우 팝업 표시
  if (!selectedZone.isEditMode) {
    emitter.emit('showPopAlert', {
      title: '세팅 모드',
      description: '세팅모드 화면으로 이동하시겠습니까?\n세팅모드 활성화시 모바일 기기에서 컨트롤이 제한됩니다.',
      okCallback: async () => {
        const payload = { isEditMode: true };
        const result = await Axios.patch(
          ZoneApi.UPDATE_ZONE.replace(':id', selectedZone._id),
          payload,
        );
        if (result.resultCode === '0000') {
          mqttControl.onPublishZone(selectedZone._id, payload);
          store.dispatch('home/fetchAllZones');
          
          // 세팅모드 설정 후 라우터 이동
          if (selectedController.value.split('_').length > 1) {
            router.push('/settings/change/controller/light/' + selectedController.value);
          } else {
            router.push('/settings/change/controller/temp/' + selectedController.value);
          }
        } else {
          emitter.emit('showPopAlert', {
            description: result.resultMessage || '세팅모드 활성화에 실패했습니다.',
          });
        }
      },
      cancelCallback: () => {}
    });
    return;
  }

  // 세팅모드가 이미 활성화되어 있는 경우 바로 이동
  if (selectedController.value.split('_').length > 1) {
    router.push('/settings/change/controller/light/' + selectedController.value);
  } else {
    router.push('/settings/change/controller/temp/' + selectedController.value);
  }
}

onBeforeMount(() => {   
  emitter.emit('onChangeHeaderSetting',{title:'컨트롤러 교체',isShowMenu:false,isShowZone:false,isShowProfile:false});    
});
onBeforeUnmount(() =>{
  emitter.emit('onChangeHeaderSetting',{title:'',isShowMenu:true,isShowZone:true,isShowProfile:true});  
})
</script>

<style scoped>
/* instruction-card 스타일 */
.instruction-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.instruction-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.instruction-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.instruction-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
}

.instruction-body {
  padding: 0.75rem 1rem;
}

.instruction-step {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.instruction-step:last-child {
  margin-bottom: 0;
}

.step-number {
  width: 1.5rem;
  height: 1.5rem;
  min-width: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #0d6efd;
  color: white;
  border-radius: 50%;
  font-weight: 600;
  font-size: 0.75rem;
}

.step-content {
  flex: 1;
  padding-top: 0.1rem;
  font-size: 0.85rem;
  line-height: 1.6;
  color: #495057;
}

.step-content strong {
  color: #0d6efd;
  font-weight: 600;
}

.instruction-warning {
  font-size: 0.85rem;
  line-height: 1.6;
}

.mesh-waiting-overlay {
  position: fixed;
  inset: 0;
  z-index: 14000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.45);
  padding: 1rem;
}

.mesh-waiting-card {
  width: 100%;
  max-width: 360px;
  background-color: #ffffff;
  border-radius: 1.2rem;
  padding: 1.5rem 1.25rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.mesh-waiting-spinner {
  display: block;
  width: 2.5rem;
  height: 2.5rem;
  margin: 0 auto 1rem;
}

.mesh-waiting-text {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.6;
  color: #495057;
  text-align: center;
}

.mesh-waiting-text + .mesh-waiting-text {
  margin-top: 0.5rem;
}
</style>
